
import { Hourglass, Check, Loader2, AlertTriangle } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

export type PhaseStatus = "pending" | "in-progress" | "completed" | "error";

export interface PhaseStatusIndicatorProps {
  status: PhaseStatus;
  phase: number;
  description?: string;
  className?: string;
}

export const getPhaseStatusText = (status: PhaseStatus): string => {
  switch (status) {
    case "pending":
      return "Waiting";
    case "in-progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "error":
      return "Error";
    default:
      return "Unknown";
  }
};

export const PhaseStatusIndicator = ({ 
  status, 
  phase, 
  description, 
  className = "" 
}: PhaseStatusIndicatorProps) => {
  const getIconAndClass = () => {
    switch (status) {
      case "pending":
        return {
          icon: <Hourglass className="h-5 w-5" />,
          colorClass: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
        };
      case "in-progress":
        return {
          icon: <Loader2 className="h-5 w-5 animate-spin" />,
          colorClass: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
        };
      case "completed":
        return {
          icon: <Check className="h-5 w-5" />,
          colorClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
        };
      case "error":
        return {
          icon: <AlertTriangle className="h-5 w-5" />,
          colorClass: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
        };
      default:
        return {
          icon: <Hourglass className="h-5 w-5" />,
          colorClass: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
        };
    }
  };

  const { icon, colorClass } = getIconAndClass();

  const tooltipContent = description || `Phase ${phase}: ${getPhaseStatusText(status)}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-2 ${className}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${colorClass}`}>
              {icon}
            </div>
            <div className="text-sm">
              <span className="font-medium">Phase {phase}:</span> {getPhaseStatusText(status)}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-2">
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
